import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const PlannersPage = () => {
  return (
    <div data-testid="planners-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1768508947362-bca7a379e62a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBnYWxhJTIwZXZlbnQlMjBlbGVnYW50JTIwYXRtb3NwaGVyZXxlbnwwfHx8fDE3NjkzMjU0NDN8MA&ixlib=rb-4.1.0&q=85"
            alt="Elegant event setup"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F] via-transparent to-[#0F0F0F]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6 animate-fade-in">
            For Industry Professionals
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F4F2EE] mb-6 animate-fade-in animate-delay-100" data-testid="planners-heading">
            Built for Professional Workflows
          </h1>
          <p className="max-w-2xl mx-auto text-[#F4F2EE]/60 leading-relaxed animate-fade-in animate-delay-200">
            Designed for seamless integration into your production process. Reliable, adaptable, and professionally executed—every time.
          </p>
        </div>
      </section>

      {/* For Whom Section */}
      <section className="py-24 md:py-32 px-6 lg:px-12" data-testid="for-whom-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-4">
                Who This Is For
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#F4F2EE] mb-8">
                Designed for Decision Makers
              </h2>
              
              <div className="space-y-4">
                {[
                  "Wedding Planners & Coordinators",
                  "Corporate Event Producers",
                  "Venue Directors & Managers",
                  "Private Concierge Services",
                  "Executive Assistants & Event Teams",
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-4" data-testid={`audience-item-${index}`}>
                    <div className="w-6 h-6 border border-[#B8A369]/50 flex items-center justify-center">
                      <Check size={14} className="text-[#B8A369]" />
                    </div>
                    <p className="text-[#F4F2EE]/80">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#0A0A0A] p-8 md:p-12 border border-[#B8A369]/10">
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
                Professional Standards
              </p>
              
              <div className="space-y-6 text-[#F4F2EE]/70 text-sm leading-relaxed" data-testid="standards-content">
                <p>
                  Working with experienced event professionals requires a particular standard of reliability and communication. Every detail matters—from load-in timing to volume levels during speeches.
                </p>
                <p>
                  This understanding informs every engagement. Expect clear communication before, during, and after your event. Expect punctuality. Expect adaptability when timelines shift.
                </p>
                <p>
                  Most importantly, expect a professional who understands their role: to enhance your carefully crafted event, not to complicate it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-[#0A0A0A]" data-testid="expectations-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-4">
              Working Together
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#F4F2EE] mb-6">
              What You Can Expect
            </h2>
            <div className="line-gold w-24 mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Pre-Event Communication",
                points: [
                  "Detailed timeline review",
                  "Repertoire consultation",
                  "Technical requirements confirmed",
                  "Direct point of contact",
                ],
              },
              {
                title: "Day-Of Reliability",
                points: [
                  "Early arrival for setup",
                  "Professional presentation",
                  "Adherence to all cues",
                  "Adaptability to changes",
                ],
              },
              {
                title: "Performance Standards",
                points: [
                  "Volume appropriate to moment",
                  "Seamless transitions",
                  "No announcements unless requested",
                  "Discreet break periods",
                ],
              },
              {
                title: "Technical Preparedness",
                points: [
                  "Professional-grade equipment",
                  "Backup systems in place",
                  "Self-sufficient setup",
                  "Clean, minimal footprint",
                ],
              },
              {
                title: "Professional Integration",
                points: [
                  "Coordination with other vendors",
                  "Respect for venue guidelines",
                  "Timeline synchronization",
                  "Post-event discretion",
                ],
              },
              {
                title: "After the Event",
                points: [
                  "Prompt load-out",
                  "Follow-up communication",
                  "Available for future planning",
                  "Referral availability",
                ],
              },
            ].map((section, index) => (
              <div key={index} className="p-6 border border-[#F4F2EE]/10" data-testid={`expectation-card-${index}`}>
                <h3 className="font-serif text-lg text-[#F4F2EE] mb-4">
                  {section.title}
                </h3>
                <div className="space-y-2">
                  {section.points.map((point, pIndex) => (
                    <div key={pIndex} className="flex items-start space-x-3">
                      <span className="text-[#B8A369] text-xs mt-1.5">—</span>
                      <p className="text-sm text-[#F4F2EE]/60">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Statement */}
      <section className="py-24 px-6 lg:px-12" data-testid="trust-section">
        <div className="max-w-3xl mx-auto">
          <div className="border border-[#B8A369]/20 p-10 md:p-14 text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
              A Note for Planners
            </p>
            <p className="font-serif text-xl md:text-2xl text-[#F4F2EE]/80 italic leading-relaxed mb-6" data-testid="trust-quote">
              "Your reputation is built on flawless execution. Mine is built on never compromising yours."
            </p>
            <div className="line-gold w-16 mx-auto mb-6" />
            <p className="text-sm text-[#F4F2EE]/50">
              Every engagement is approached with the understanding that your client's experience—and your professional standing—depends on every vendor performing at the highest level.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#0A0A0A]" data-testid="planners-cta">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
            Let's Work Together
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#F4F2EE] mb-6">
            Check Availability for Upcoming Events
          </h2>
          <p className="text-[#F4F2EE]/60 leading-relaxed mb-10">
            Share your event details and let's discuss how to integrate live music into your production seamlessly.
          </p>
          <Link
            to="/inquiry"
            className="btn-luxury"
            data-testid="planners-cta-button"
          >
            Submit Event Inquiry
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PlannersPage;
